import {
    ArchiveBoxIcon,
    ArrowUturnLeftIcon,
    CheckCircleIcon,
    EyeIcon,
    PencilIcon,
    TrashIcon,
    XMarkIcon
} from "@heroicons/react/24/outline";
import {classNames} from "../../util/util-helpers";
import React from "react";
import {toFrontDate, toFrontDateTime, toFrontDateTimeFromUTC} from "../../util/util-dates";
import {formatMoney} from "../../../util/util";
import {genericMoneyFormatter} from "../../util/util-vanilla";
import {numberWithCommas} from "../../../util/util-formaters";
import Tooltip from "../../../components/tooltip";

const rightAlignedFiledTypes = ['integer', 'money', 'float']; // add more
const centerAlignedFiledTypes = ['checkbox', 'date', 'datetime', 'datetimezone'];

function sortHeaders(a, b, options) {
    return options?.columns?.[a?.name]?.order - options?.columns?.[b?.name]?.order;
}

function getColumnAlignment(fieldType) {
    if (rightAlignedFiledTypes.includes(fieldType)) {
        return "right";
    }

    if (centerAlignedFiledTypes.includes(fieldType)) {
        return "center";
    }

    return "left"
}

export function mergeCRUDActions(actions, onView, onEdit, onDelete, onRestore, translate) {
    let actionsClone = [...actions];

    if (!actionsClone) {
        actionsClone = [];
    }

    if (onView) {
        actionsClone.push({
            action: onView,
            icon: EyeIcon,
            order: 10,
            key: "view",
            title: translate("btn.view")
        });
    }

    if (onEdit) {
        actionsClone.push({
            action: onEdit,
            icon: PencilIcon,
            order: 20,
            key: "edit",
            title: translate("btn.edit")
        });
    }

    if (onDelete) {
        actionsClone.push({
            action: onDelete,
            icon: onRestore ? ArchiveBoxIcon : TrashIcon,
            visible: (item) => !item["ArchivedDate"],
            order: 30,
            key: "delete",
            title: onRestore ? translate("btn.archive") : translate("btn.delete")
        });
    }

    if (onRestore) {
        actionsClone.push({
            action: onRestore,
            icon: ArrowUturnLeftIcon,
            visible: (item) => !!item["ArchivedDate"],
            order: 40,
            key: "restore",
            title: translate("btn.restore")
        });
    }

    return actionsClone;
}

function ButtonIcon({button, rowData, rowKey, className}) {
    if (!button.icon) {
        return null;
    }

    let buttonIconClass = className ?? "w-5 h-5";

    if (button.iconClass) {
        buttonIconClass = typeof button.iconClass === 'function'
            ? button.iconClass(rowData, rowKey)
            : button.iconClass
    }

    if (typeof button.icon !== 'function') {
        return <button.icon
            className={buttonIconClass}
        />
    }

    if (typeof button.icon === 'function') {
        let ButtonIcon = button.icon(rowData, rowKey);

        return <ButtonIcon
            className={buttonIconClass}
        />
    }
}

export function ButtonsActions({actions, rowData, buttonClass, rowKey}) {
    return (
        <div className="space-x-2 whitespace-nowrap">
            {actions
                .filter(button => button.visible === undefined || button.visible(rowData, rowKey))
                .sort((a, b) => a.order - b.order)
                .map((button, btnIndex) => {
                    const hasTooltip = typeof button.tooltipText === "function";
                    return (
                        <Tooltip
                            key={button.key ?? btnIndex}
                            content={hasTooltip && button.tooltipText()}
                            disabled={!hasTooltip}
                        >
                            <button
                                disabled={button.disabled}

                                onClick={(e) => {
                                    e.stopPropagation();
                                    !!button.action && button.action(rowData, rowKey)
                                }}
                                className={button.class ? button.class : buttonClass}
                                title={typeof button.title === "function" ? button.title(rowData, rowKey) : button.title}
                            >
                                <ButtonIcon
                                    className={
                                        classNames(
                                            "w-5 h-5",
                                        )
                                    }
                                    button={button}
                                    rowData={rowData}
                                    rowKey={rowKey}
                                />

                                {!!button.customContent && button.customContent(rowData, rowKey)}

                                {
                                    typeof button.label === "function"
                                        ? <span
                                            className={button.iconPosition === 'right' && "order-1"}>
                                        {() => button.label(rowData, rowKey)}
                                    </span>

                                        : <span className={button.iconPosition === 'right' ? "order-1" : undefined}>
                                        {button.label}
                                    </span>
                                }
                            </button>
                        </Tooltip>
                    )
                })
            }
        </div>
    )
}

export function getDefaultRowMinHeight(options) {
    if (options?.style?.superCondensed) {
        return 24;
    }

    if (options?.style?.condensed) {
        return 36;
    }

    return 48;
}

export function getHeadersData(fields, options, headersRefs, onSelectRow, onSortChange, mergedActions, tableID, translate) {
    let headers = Object.values(fields)
        .filter(it => !it?.metadata?.hideTable && options?.columns?.[it.name]?.show !== false);

    headers = headers.sort((a, b) => sortHeaders(a, b, options))
        .reduce((memo, it, i) => {
            let isFrozen = options?.columns?.[it.name]?.frozen;
            //const isSortable = !!onSortChange && !it?.metadata?.omitSort; // hardCoded for now because of the menu testing

            let additionalClass = !onSelectRow && !i ? ' tc-first' : '';
            additionalClass = additionalClass + (headers.length - 1 === i && options?.style?.floatingActions ? ' tc-last' : '')

            const keyPlusID = tableID + "-" + it.name;
            let header = {
                order: i,
                key: it.name,
                keyPlusID: keyPlusID,
                content: options?.columns[it.name]?.label ?? translate(it.name),
                class: options?.behaviour?.hasVirtualRows
                    ? `vth-${tableID}-default vth-` + keyPlusID + additionalClass
                    : `th-${tableID}-default th-` + keyPlusID + additionalClass,
                cellClass: options?.behaviour?.hasVirtualRows
                    ? `vtc-${tableID}-default vtc-` + keyPlusID + additionalClass
                    : `tc-${tableID}-default tc-` + keyPlusID + additionalClass,
                style: {},
                align: getColumnAlignment(it.type),
                ref: (element) => headersRefs.current[it.name] = element,
                subColumns: options?.columns?.[it.name]?.subColumns?.reduce((memo, it) => {
                    memo.push(Object.values(it)?.[0]);
                    return memo;
                }, []),
                isFrozen: isFrozen,
                isTruncate: true,
                canAdjustWidth: options?.behaviour?.canAdjustWidth ?? false,
                isSortable: !!onSortChange && !it?.metadata?.omitSort,
                hasMenu: options?.behaviour?.hasMenu ?? false
            }

            memo.push(header);
            return memo;
        }, []);

    if (onSelectRow) {
        headers.unshift({
            key: "select-row-column",
            keyPlusID: tableID + "-select-row-column",
            class: "vth-select-row-column",
            isFrozen: headers?.[0]?.isFrozen,
            cellClass: "vtc-select-row-column",
            columnWidth: 48
        });
    }

    let actionsColWidth = 0;
    if (mergedActions.length) {
        let actionsCount = mergedActions.length;

        if (mergedActions.find(it => it.key === 'restore')) {
            actionsCount = actionsCount - 1;
        }

        actionsColWidth = (actionsCount * 36) + ((actionsCount + 1) * 8);

        headers.push({
            key: "actions",
            keyPlusID: tableID + "-actions",
            isFloating: !!options?.style?.floatingActions,
            class: "vth-" + tableID + "-actions",
            cellClass: "vtc-" + tableID + "-actions",
            content: "Actions",
            isFrozen: options?.style?.frozenActionColumn || options?.style?.floatingActions,
            style: {
                // width: options?.style?.floatingActions ? 0 : actionsColWidth + "px",
                // minWidth: options?.style?.floatingActions ? 0 : actionsColWidth + "px",
                // padding: options?.style?.floatingActions ? 0 : actionsColWidth + "px"
                width: 0,
                minWidth: 0,
                padding: 0
            },
            columnWidth: options?.style?.floatingActions ? 0 : actionsColWidth
        });
    }

    return headers;
}

export function fieldToCell(fields, name, item, modifiedItem) {
    let field = fields[name];
    let value = item[name];

    if (fields[name]?.metadata?.render) {
        return fields[name].metadata.render(item);
    }

    switch (field?.type) {
        case 'hidden':
            return null;
        case 'date':
            return toFrontDate(value);
        case 'datetime':
            return toFrontDateTime(value);
        case 'datetimezone':
            return toFrontDateTimeFromUTC(value);
        case 'checkbox':
        case 'switch':
            // yes, no, empty, customize labels and icons (make default imports)
            return value
                ? <CheckCircleIcon className="w-5 h-5 text-green-600 inline"/>
                : <XMarkIcon className="w-5 h-5 text-tm-gray-400 inline"/>

        case 'select':
            return modifiedItem[name + "_select"];
        case 'select-search':
            return item[name.replace("ID", "")];
        case 'float':
        case 'float_up_to_12_not_require':
        case 'float_not_require':
        case 'float_up_to_100':
            return numberWithCommas(value)
        case 'integer':
        case 'integer_or_empty':
        case 'integer_not_require':
        case 'integer_up_to_100':
        case 'integer_up_to_256':
            return formatMoney(value, 0, ".", ",")
        case 'money':
            return genericMoneyFormatter(value, 0, ".", ",")
        default:
            return value;
    }
}

export function getColumns(fields, translate) {
    return Object.values(fields).filter(field => !field?.metadata?.hideTable).reduce((memo, it, index) => {
        const defaultLabel = !!fields[it.name]?.metadata?.label ? translate("field." + fields[it.name].metadata.label) : translate("field." + it.name);

        memo[it.name] = {
            name: it.name,
            label: defaultLabel,
            frozen: !index,
            type: it.type,
            show: true,
            showMobile: index < 4,
            isMerged: false,
            merged: [],
            minWidth: fields[it.name].metadata?.minWidth
        };
        return memo
    }, {});
}

export function mergeColumns(options, tableCustomDefaults) {
    return Object.keys(options["columns"]).reduce((columnsMemo, column) => {
        if (!!tableCustomDefaults["columns"][column]) {
            columnsMemo[column] = Object.assign({}, options["columns"][column], tableCustomDefaults["columns"][column]);
        } else {
            columnsMemo[column] = options["columns"][column];
        }

        return columnsMemo;
    }, {});
}

